import { formatDate as formatDisplayDate } from '@/lib/formatDate';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { Prescription, PrescriptionDataTransformer } from '@/types/prescription';
import { Patient } from '@/types';
import { Doctor } from '@/types/doctor';
import { getPrescriptionById, getPatient, getSettings } from '@/lib/api';
import PrescriptionForm from '@/components/Prescription/PrescriptionForm';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import { PatientBanner } from '@/components/Patient/PatientBanner';
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString?: Date | string): string => {
  if (!dateString) return 'Unknown date';
  return formatDisplayDate(dateString) || 'Invalid date';
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function EditPrescriptionPage() {
  usePageTitle('Edit prescription');
  const { id: prescriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Where the edit was opened from (the patient page passes itself); the Prescriptions list otherwise
  const returnTo: string = (useLocation().state as { returnTo?: string } | null)?.returnTo || '/prescriptions';
  const { currentUser } = useAuth();

  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printRx, setPrintRx] = useState<Prescription | null>(null);

  // ── Load prescription, then patient + doctor ───────────────────────────────

  useEffect(() => {
    if (!prescriptionId) {
      setError('No prescription ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getPrescriptionById(prescriptionId);
        if (cancelled) return;

        if (!data) {
          setError('Prescription not found');
          return;
        }
        setPrescription(data);

        const patientId = data.patient_id;
        if (patientId) {
          getPatient(patientId)
              .then((p) => { if (!cancelled && p) setPatient(p as Patient); })
              .catch(() => {/* leave null — form still works without it */});
        }

        if (currentUser?.doctor_id && currentUser?.clinic_id) {
          getSettings(currentUser.doctor_id, currentUser.clinic_id)
              .then((s) => {
                if (cancelled || !s) return;
                setDoctor({
                  id:            currentUser.doctor_id ?? '',
                  name:          s.profile?.name       ?? '',
                  specialty:     s.profile?.specialty  ?? 'General Medicine',
                  phone:         s.profile?.phone      ?? '',
                  email:         s.profile?.email      ?? '',
                  clinic_id:     currentUser.clinic_id ?? '',
                  last_modified: '',
                });
              })
              .catch(() => {/* leave null — specialty defaults to General Medicine */});
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading prescription:', err);
          setError('Failed to load prescription');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [prescriptionId, currentUser?.doctor_id, currentUser?.clinic_id]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleClose = () => navigate(returnTo);

  const handleSaved = (saved?: Prescription) => {
    toast({
      title: 'Success',
      description: 'Prescription updated successfully',
      variant: 'default',
    });
    // "Update & print": the print preview first, back where the edit started once it's closed
    if (saved) setPrintRx(saved);
    else navigate(returnTo);
  };

  // ── Guard states ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescription...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Error Loading Prescription</h3>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => window.location.reload()} variant="outline">
                  Try Again
                </Button>
                <Button onClick={handleClose}>
                  Go Back
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Prescription Not Found</h3>
              <p className="text-muted-foreground mb-6">
                The prescription you're looking for doesn't exist or couldn't be loaded.
              </p>
              <Button onClick={handleClose}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (prescription.edit_policy && !prescription.edit_policy.allowed) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">This prescription can't be changed</h3>
              <p className="text-muted-foreground mb-6">{prescription.edit_policy.message}</p>
              <Button onClick={handleClose}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const patientId   = prescription.patient_id || '';
  const displayName = patient?.name || prescription.patient_name || 'Patient';

  return (
    // No page gutter: the patient header runs edge to edge under the top bar, as in the mockup
    <div className="min-h-screen bg-background">

      {/* Sticky header — mirrors PatientDetailPage's patient header for a consistent edit experience */}
      <div
        className="sticky z-30 bg-card border-b border-border"
        style={{ top: 'var(--topbar-height, 60px)' }}
      >
        <PatientBanner
          size="compact"
          patient={{ ...(patient ?? {}), name: displayName }}
          meta={<>
            <span>Prescription of {formatDate(prescription.date)}</span>
            {doctor?.specialty && <><span aria-hidden>·</span><span>{doctor.specialty}</span></>}
          </>}
          leading={
            <Button variant="outline" size="sm" onClick={handleClose} className="gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          }
          actions={
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
              Editing prescription
            </span>
          }
        />
      </div>

      {/* Form — rendered inline (same pattern as the new-prescription flow on PatientDetailPage) */}
      <div className="px-4 sm:px-6">
        <PrescriptionForm
          mode="edit"
          patientId={patientId}
          initialData={PrescriptionDataTransformer.toFormData(prescription, patientId)}
          onSaved={handleSaved}
          onCancel={handleClose}
          showStickyBar={true}
          patient={patient ?? undefined}
          doctor={doctor ?? undefined}
        />
      </div>

      <PrintPrescriptionPreviewModal open={!!printRx} prescription={printRx} onClose={() => navigate(returnTo)} />
    </div>
  );
}
